/**
 * SplitView Component
 *
 * Provides side-by-side view of original and redacted documents
 * with synchronized scrolling and resizable panes.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { GripVertical, Maximize2, Minimize2, X } from 'lucide-react';

interface SplitViewProps {
  leftPanel: React.ReactNode;
  rightPanel: React.ReactNode;
  leftTitle?: string;
  rightTitle?: string;
  onClose?: () => void;
  syncScroll?: boolean;
}

const MIN_PANEL_WIDTH = 200;

export function SplitView({
  leftPanel,
  rightPanel,
  leftTitle = 'Original',
  rightTitle = 'Redacted',
  onClose,
  syncScroll = true,
}: SplitViewProps) {
  const [splitPosition, setSplitPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isLeftExpanded, setIsLeftExpanded] = useState(false);
  const [isRightExpanded, setIsRightExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const leftScrollRef = useRef<HTMLDivElement>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);
  const isScrollingSynced = useRef(false);

  // Handle drag to resize panels
  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const newPosition = ((e.clientX - containerRect.left) / containerRect.width) * 100;

    // Enforce minimum panel width
    const minPercent = (MIN_PANEL_WIDTH / containerRect.width) * 100;
    const maxPercent = 100 - minPercent;

    setSplitPosition(Math.max(minPercent, Math.min(maxPercent, newPosition)));
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Synchronized scrolling
  const handleScroll = useCallback((source: 'left' | 'right') => {
    if (!syncScroll || isScrollingSynced.current) return;

    const sourceRef = source === 'left' ? leftScrollRef : rightScrollRef;
    const targetRef = source === 'left' ? rightScrollRef : leftScrollRef;

    if (!sourceRef.current || !targetRef.current) return;

    isScrollingSynced.current = true;

    const sourceScrollTop = sourceRef.current.scrollTop;
    const sourceScrollLeft = sourceRef.current.scrollLeft;
    const sourceMaxScrollTop = sourceRef.current.scrollHeight - sourceRef.current.clientHeight;
    const sourceMaxScrollLeft = sourceRef.current.scrollWidth - sourceRef.current.clientWidth;
    const targetMaxScrollTop = targetRef.current.scrollHeight - targetRef.current.clientHeight;
    const targetMaxScrollLeft = targetRef.current.scrollWidth - targetRef.current.clientWidth;

    // Calculate relative scroll position
    const scrollTopPercent = sourceMaxScrollTop > 0 ? sourceScrollTop / sourceMaxScrollTop : 0;
    const scrollLeftPercent = sourceMaxScrollLeft > 0 ? sourceScrollLeft / sourceMaxScrollLeft : 0;

    // Apply to target
    targetRef.current.scrollTop = scrollTopPercent * targetMaxScrollTop;
    targetRef.current.scrollLeft = scrollLeftPercent * targetMaxScrollLeft;

    // Reset sync flag after a short delay
    requestAnimationFrame(() => {
      isScrollingSynced.current = false;
    });
  }, [syncScroll]);

  // Handle expand/collapse
  const toggleLeftExpand = () => {
    setIsLeftExpanded(!isLeftExpanded);
    setIsRightExpanded(false);
  };

  const toggleRightExpand = () => {
    setIsRightExpanded(!isRightExpanded);
    setIsLeftExpanded(false);
  };

  // Reset to 50/50 split
  const resetSplit = () => {
    setSplitPosition(50);
    setIsLeftExpanded(false);
    setIsRightExpanded(false);
  };

  const getLeftWidth = () => {
    if (isLeftExpanded) return '100%';
    if (isRightExpanded) return '0%';
    return `${splitPosition}%`;
  };

  const getRightWidth = () => {
    if (isRightExpanded) return '100%';
    if (isLeftExpanded) return '0%';
    return `${100 - splitPosition}%`;
  };

  return (
    <div ref={containerRef} className="flex h-full w-full overflow-hidden">
      {/* Left Panel */}
      <div
        className="flex flex-col transition-all duration-200"
        style={{ width: getLeftWidth(), minWidth: isLeftExpanded ? '100%' : MIN_PANEL_WIDTH }}
      >
        {/* Left Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border">
          <span className="text-sm font-medium">{leftTitle}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleLeftExpand}
              className="p-1 hover:bg-muted rounded transition-colors"
              title={isLeftExpanded ? 'Restore' : 'Expand'}
            >
              {isLeftExpanded ? (
                <Minimize2 className="w-4 h-4" />
              ) : (
                <Maximize2 className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* Left Content */}
        <div
          ref={leftScrollRef}
          className="flex-1 overflow-auto bg-muted"
          onScroll={() => handleScroll('left')}
        >
          {leftPanel}
        </div>
      </div>

      {/* Resizer */}
      {!isLeftExpanded && !isRightExpanded && (
        <div
          className={`flex items-center justify-center w-2 bg-border cursor-col-resize hover:bg-primary/50 transition-colors ${
            isDragging ? 'bg-primary' : ''
          }`}
          onMouseDown={handleMouseDown}
          onDoubleClick={resetSplit}
          title="Drag to resize. Double-click to reset."
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </div>
      )}

      {/* Right Panel */}
      <div
        className="flex flex-col transition-all duration-200"
        style={{ width: getRightWidth(), minWidth: isRightExpanded ? '100%' : MIN_PANEL_WIDTH }}
      >
        {/* Right Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border">
          <span className="text-sm font-medium">{rightTitle}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleRightExpand}
              className="p-1 hover:bg-muted rounded transition-colors"
              title={isRightExpanded ? 'Restore' : 'Expand'}
            >
              {isRightExpanded ? (
                <Minimize2 className="w-4 h-4" />
              ) : (
                <Maximize2 className="w-4 h-4" />
              )}
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-1 hover:bg-muted rounded transition-colors"
                title="Close split view"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Right Content */}
        <div
          ref={rightScrollRef}
          className="flex-1 overflow-auto bg-muted"
          onScroll={() => handleScroll('right')}
        >
          {rightPanel}
        </div>
      </div>
    </div>
  );
}
